import type { TFunction } from "i18next";

/**
 * Maps raw `error` strings from the DGI auto-flow's Python automation into
 * user-facing Spanish copy. The Python service throws English technical
 * messages; the modal/inline UI should never render those verbatim.
 *
 * Match strategy:
 *   1. Exact-substring lookup against a known map (most reliable).
 *   2. Pattern-based fallbacks that catch families of similar errors.
 *   3. Generic last-resort message that still hints at what failed.
 *
 * `test` accepts an ARRAY when the automation emits more than one wording for
 * the same failure — any one of them matches. Prefer that over loosening a
 * single substring, so each accepted wording stays traceable to the Python
 * line that produces it.
 */
const EXACT_SUBSTRING_MAP: Array<{
  test: string | string[];
  key: string;
  fallback: string;
}> = [
  {
    test: "consulta datos page still shows login required",
    key: "onboarding.autoFlow.errors.companyInfoLoginWall",
    fallback:
      "No pudimos volver a entrar al portal de DGI para extraer tus datos. Probemos otra vez.",
  },
  {
    test: "cae page still shows login required",
    key: "onboarding.autoFlow.errors.caeLoginWall",
    fallback:
      "No pudimos volver a entrar al portal de e-Factura para descargar los CAEs.",
  },
  {
    test: "portal user setup failed",
    key: "onboarding.autoFlow.errors.portalUserSetup",
    fallback:
      "No pudimos crear tu usuario e-Factura en DGI. Tu sesión puede haber caducado.",
  },
  {
    test: "could not click idp",
    key: "onboarding.autoFlow.errors.idpClick",
    fallback:
      "No pudimos seleccionar tu proveedor de identidad (Abitab o TuID).",
  },
  {
    test: "could not click permitir",
    key: "onboarding.autoFlow.errors.permitirClick",
    fallback:
      "DGI mostró una pantalla de autorización que no pudimos confirmar automáticamente.",
  },
  {
    test: "could not click continuar on aviso al contribuyente",
    key: "onboarding.autoFlow.errors.avisoContinuar",
    fallback:
      "No pudimos confirmar el aviso al contribuyente de DGI.",
  },
  {
    test: "could not find a table row matching the company rut",
    key: "onboarding.autoFlow.errors.entityRowNotFound",
    fallback:
      "DGI no nos mostró tu empresa entre las entidades vinculadas. Verificá que el RUT esté asociado a tu identidad digital.",
  },
  {
    test: "cancelled by user",
    key: "onboarding.autoFlow.errors.cancelled",
    fallback: "Cancelaste el proceso.",
  },
  {
    test: "missing document number",
    key: "onboarding.autoFlow.errors.missingDocumentNumber",
    fallback:
      "No recibimos el número de documento para completar la autenticación.",
  },
  {
    test: "missing tuid document",
    key: "onboarding.autoFlow.errors.missingTuidDocument",
    fallback: "No recibimos tu documento TuID.",
  },
  {
    test: "no identity provider selected",
    key: "onboarding.autoFlow.errors.noIdp",
    fallback: "No elegiste un proveedor de identidad para continuar.",
  },
  {
    // efactura_portal_user_flow.py:294 "Could not click Borrar usuario submit",
    // :240 "Could not click 'Borrar usuario' link: {e}" (quotes are stripped
    // before matching), :584 "Borrar usuario flow failed".
    test: ["could not click borrar usuario", "borrar usuario flow failed"],
    key: "onboarding.autoFlow.errors.borrarUsuario",
    fallback:
      "No pudimos eliminar el usuario e-Factura previo. Probá la configuración manual.",
  },
  {
    // efactura_portal_user_flow.py:565 "Password fields did not appear and no
    // 'Borrar usuario' link found", :556 "Password fields still did not appear
    // after deleting the existing user" — the injected "still" is why the
    // second wording needs its own entry.
    test: ["password fields did not appear", "password fields still did not appear"],
    key: "onboarding.autoFlow.errors.passwordFieldsMissing",
    fallback:
      "DGI no mostró el formulario para crear tu contraseña. Tu sesión puede haber expirado.",
  },
  {
    test: "could not reload solicitud",
    key: "onboarding.autoFlow.errors.reloadFailed",
    fallback:
      "Perdimos la sesión con DGI mientras regenerábamos tu usuario.",
  },
  {
    test: "could not store pdf",
    key: "onboarding.autoFlow.errors.storePdf",
    fallback: "No pudimos guardar los certificados descargados.",
  },
  {
    test: "actualización de datos form not found",
    key: "onboarding.autoFlow.errors.proveedorFormNotFound",
    fallback:
      "No pudimos abrir el formulario de Actualización de datos en e-Factura.",
  },
  {
    test: "agregar software modal iframe",
    key: "onboarding.autoFlow.errors.proveedorModalNotOpened",
    fallback:
      "DGI no abrió la ventana para agregar tu software. Probemos otra vez.",
  },
  {
    test: "actualización de datos iframe context not found",
    key: "onboarding.autoFlow.errors.proveedorIframeNotFound",
    fallback:
      "Perdimos el contexto del formulario de Actualización de datos. Probemos otra vez.",
  },
  {
    test: "could not click confirmar producto",
    key: "onboarding.autoFlow.errors.proveedorConfirmProduct",
    fallback:
      "No pudimos confirmar el producto Fiscalo en el formulario de DGI.",
  },
  {
    test: "could not click confirmar proceso",
    key: "onboarding.autoFlow.errors.proveedorConfirmProcess",
    fallback:
      "No pudimos confirmar el proceso de Fiscalo en el formulario de DGI.",
  },
  {
    test: "could not click confirmar datos",
    key: "onboarding.autoFlow.errors.proveedorConfirmData",
    fallback:
      "No pudimos confirmar los datos de Fiscalo en e-Factura.",
  },
  {
    test: "could not click confirmación final",
    key: "onboarding.autoFlow.errors.proveedorConfirmFinal",
    fallback:
      "No pudimos confirmar la pantalla final de Fiscalo en e-Factura.",
  },
  {
    test: "session lost",
    key: "onboarding.autoFlow.errors.sessionLost",
    fallback:
      "Tu sesión con DGI se cerró antes de terminar. Probá de nuevo.",
  },
  {
    test: "failed to start",
    key: "onboarding.autoFlow.errors.startFailed",
    fallback:
      "No pudimos iniciar la automatización con DGI. Probá de nuevo en unos segundos.",
  },
  {
    test: "timeout",
    key: "onboarding.autoFlow.errors.timeout",
    fallback:
      "DGI está respondiendo más lento de lo habitual. Probá de nuevo o usá la configuración manual.",
  },
];

export function translateAutoFlowStepError(
  raw: string | undefined,
  t: TFunction,
): string {
  const clean = (raw ?? "").trim();
  if (!clean) {
    return t(
      "onboarding.autoFlow.errors.generic",
      "No pudimos terminar este paso. Probemos de nuevo.",
    );
  }
  // Quotes are stripped, not just lowercased: the automation quotes DGI control
  // names inconsistently ("Could not click 'Borrar usuario' link" vs "Could not
  // click Borrar usuario submit"), and an unstripped quote silently breaks the
  // substring match — the user then gets the generic message instead of the
  // advice for that step.
  const lower = clean.toLowerCase().replace(/['"`‘’“”]/g, "");
  for (const entry of EXACT_SUBSTRING_MAP) {
    const wordings = Array.isArray(entry.test) ? entry.test : [entry.test];
    if (wordings.some((wording) => lower.includes(wording))) {
      return t(entry.key, entry.fallback);
    }
  }
  // Unknown error — keep the user oriented without surfacing the raw English.
  return t(
    "onboarding.autoFlow.errors.generic",
    "No pudimos terminar este paso. Probemos de nuevo.",
  );
}
